import { useLayoutEffect, useState } from "react";
import { FOOTER_BAND_TOLERANCE } from "../helpers/layoutConstants";

// Decides how an anchored footer should render: "fixed" (always visible at
// the viewport bottom — the classic look) while the page has no vertical
// overflow, or "flow" (in normal flow at the end of the content) once the
// page scrolls, so the fixed bar never covers the last lines of content.
// flowGap: extra in-flow space the footer carries in flow mode (e.g. the
// copyright bar's marginTop) — counted so the mode is a pure function of
// content height instead of being path-dependent across mode switches.
const useFooterMode = (footerHeight, flowGap = 0) => {
  const [mode, setMode] = useState("fixed");

  useLayoutEffect(() => {
    // AppContent's root is the app's single scroll container.
    const scroller = document.querySelector(".bg-alice-blue");
    if (!scroller) {
      console.warn(
        "useFooterMode: scroll container .bg-alice-blue not found — footer stays anchored",
      );
      return undefined;
    }

    let current = null;
    const update = () => {
      // In flow mode the footer and its gap add to scrollHeight — subtract
      // the full footprint so contentHeight (and therefore the mode) is the
      // same regardless of which mode we are currently in. Under-subtracting
      // traps pages in flow: a transient overflow during mount (carousel
      // init, font load) would otherwise stick as a permanent idle scroll.
      const flowFooter = current === "flow" ? footerHeight + flowGap : 0;
      const contentHeight = scroller.scrollHeight - flowFooter;
      const viewport = scroller.clientHeight;
      const overflows = contentHeight > viewport + FOOTER_BAND_TOLERANCE;
      // Content ending inside the bar's overlay zone would be covered with
      // no scrollbar to escape — flip to flow. FOOTER_BAND_TOLERANCE keeps stretched
      // 100vh pages anchored: their scrollHeight/clientHeight round
      // independently under zoom/display scaling and can disagree by a
      // couple px, which must not read as intrusion. Pages must end at
      // least footerHeight + FOOTER_BAND_TOLERANCE above the viewport bottom (the
      // profile panel budgets in layoutConstants account for this).
      const intrudesUnderBar =
        contentHeight > viewport - footerHeight + FOOTER_BAND_TOLERANCE &&
        contentHeight < viewport - FOOTER_BAND_TOLERANCE;
      const next = overflows || intrudesUnderBar ? "flow" : "fixed";
      if (next !== current) {
        current = next;
        setMode(next);
      }
    };

    const resizeObserver = new ResizeObserver(update);
    const observeChildren = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(scroller);
      Array.from(scroller.children).forEach((el) => resizeObserver.observe(el));
      update();
    };
    observeChildren();
    // Tab/route switches replace the scroller's children with elements the
    // ResizeObserver has never seen (e.g. the dashboard footer outlives the
    // tab content next to it) — re-attach on child-list changes or the
    // measurements go stale until a full reload.
    const mutationObserver = new MutationObserver(observeChildren);
    mutationObserver.observe(scroller, { childList: true });
    window.addEventListener("resize", update);
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [footerHeight, flowGap]);

  return mode;
};

export default useFooterMode;
